import React, { useState } from 'react';
import styled from 'styled-components';
import { Download, Search, X } from 'lucide-react';
import debounce from 'helpers/debounce';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import { PERIOD_OPTIONS, STATUS_OPTIONS, PAYMENT_OPTIONS, CUSTOM_PERIOD } from './orderTokens';

/**
 * Controles da aba Pedidos.
 *
 * O conjunto é o mesmo do Financeiro de propósito (chips de período, selects de
 * recorte, exportar à direita): quem já filtrou lá não reaprende nada aqui. O
 * que muda é o que não está — não há seletor de evento, a aba já vive dentro de um.
 *
 * Todos os filtros são resolvidos pelo backend, então a contagem exibida é a
 * real, e não o que sobrou da página já carregada.
 */
export default function EventOrdersFilters({
    filters,
    onChange,
    onExport,
    exporting,
    exportError,
    canExport,
    searchable,
}) {
    const [range, setRange] = useState({ start: '', end: '' });
    const [term, setTerm] = useState(filters?.search || '');

    const activePeriod = filters?.start_date ? CUSTOM_PERIOD : (filters?.period || 'all');
    const showRange = activePeriod === CUSTOM_PERIOD;

    /* O campo é controlado localmente e só solta a busca depois da pausa: sem
       isso cada tecla vira uma requisição e a lista pisca no meio da digitação. */
    const pushSearch = React.useMemo(
        () => debounce((value) => onChange({ ...filters, search: value || undefined }), 500),
        [filters, onChange]
    );

    function changeTerm(value) {
        setTerm(value);
        pushSearch(value.trim());
    }

    function clearTerm() {
        setTerm('');
        onChange({ ...filters, search: undefined });
    }

    function selectPeriod(period) {
        if (period === CUSTOM_PERIOD) {
            setRange({ start: '', end: '' });
            onChange({ ...filters, period: CUSTOM_PERIOD, start_date: undefined, end_date: undefined });
            return;
        }

        onChange({ ...filters, period, start_date: undefined, end_date: undefined });
    }

    function changeRange(field, value) {
        const next = { ...range, [field]: value };
        setRange(next);

        // O backend exige as duas pontas: só dispara quando o intervalo fecha.
        if (next.start && next.end) {
            onChange({ ...filters, period: CUSTOM_PERIOD, start_date: next.start, end_date: next.end });
        }
    }

    function changeField(field, value) {
        onChange({ ...filters, [field]: value });
    }

    return (
        <Bar>
            {searchable && (
                <SearchField>
                    <Search size={15} color={colors.night} aria-hidden="true" />
                    <SearchInput
                        type="search"
                        value={term}
                        placeholder="Buscar por comprador, e-mail ou número do pedido"
                        aria-label="Buscar pedidos por comprador, e-mail ou número do pedido"
                        onChange={(e) => changeTerm(e.target.value)}
                    />
                    {term && (
                        <ClearButton type="button" onClick={clearTerm} aria-label="Limpar busca">
                            <X size={14} />
                        </ClearButton>
                    )}
                </SearchField>
            )}

            <Line>
                <GroupLabel id="event-orders-period">Período</GroupLabel>
                <Chips role="group" aria-labelledby="event-orders-period">
                    {PERIOD_OPTIONS.map(option => (
                        <Chip
                            key={option.value}
                            type="button"
                            aria-pressed={activePeriod === option.value}
                            $selected={activePeriod === option.value}
                            onClick={() => selectPeriod(option.value)}
                        >
                            {option.label}
                        </Chip>
                    ))}
                    <Chip
                        type="button"
                        aria-pressed={activePeriod === CUSTOM_PERIOD}
                        $selected={activePeriod === CUSTOM_PERIOD}
                        onClick={() => selectPeriod(CUSTOM_PERIOD)}
                    >
                        Personalizado
                    </Chip>
                </Chips>

                <ExportButton
                    type="button"
                    onClick={onExport}
                    disabled={exporting || !canExport}
                    title={canExport ? 'Baixar os pedidos filtrados em CSV' : 'Nada para exportar com os filtros atuais'}
                >
                    <Download size={14} />
                    {exporting ? 'Gerando...' : 'Exportar CSV'}
                </ExportButton>
            </Line>

            {showRange && (
                <Line>
                    <GroupLabel as="span">Intervalo</GroupLabel>
                    <DateInput
                        type="date"
                        value={range.start}
                        max={range.end || undefined}
                        onChange={(e) => changeRange('start', e.target.value)}
                        aria-label="Data inicial"
                    />
                    <Separator>até</Separator>
                    <DateInput
                        type="date"
                        value={range.end}
                        min={range.start || undefined}
                        onChange={(e) => changeRange('end', e.target.value)}
                        aria-label="Data final"
                    />
                </Line>
            )}

            <Line>
                <Select
                    value={filters?.status || ''}
                    onChange={(e) => changeField('status', e.target.value)}
                    aria-label="Filtrar por status do pedido"
                >
                    {STATUS_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </Select>

                <Select
                    value={filters?.payment_method || ''}
                    onChange={(e) => changeField('payment_method', e.target.value)}
                    aria-label="Filtrar por forma de pagamento"
                >
                    {PAYMENT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </Select>

                {exportError && (
                    <ExportError role="alert">Não foi possível gerar o arquivo. Tente novamente.</ExportError>
                )}
            </Line>
        </Bar>
    );
}

const Bar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid ${colors.neutralDark};

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 14px 20px;
    }
`;

const Line = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

const SearchField = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 10px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};

    &:focus-within {
        border-color: ${colors.secondaryPurple};
        box-shadow: 0 0 0 3px #F7EFFF;
    }
`;

const SearchInput = styled.input`
    flex: 1;
    min-width: 0;
    padding: 9px 0;
    border: none;
    outline: none;
    background: transparent;
    color: ${colors.nightDark};
    font-size: 13px;
    font-family: inherit;

    &::placeholder {
        color: ${colors.nightLight};
    }

    /* O "x" nativo do type=search duplicaria o botão de limpar ao lado. */
    &::-webkit-search-cancel-button {
        display: none;
    }
`;

const ClearButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    border-radius: 50%;
    background: ${colors.neutralDark};
    color: ${colors.night};
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        background: ${colors.neutral};
        color: ${colors.nightDark};
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const GroupLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    white-space: nowrap;
`;

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const Chip = styled.button`
    padding: 6px 12px;
    border-radius: 50px;
    border: 1px solid ${({ $selected }) => $selected ? colors.secondaryPurple : colors.neutralDark};
    background: ${({ $selected }) => $selected ? '#F7EFFF' : colors.neutralLight};
    color: ${({ $selected }) => $selected ? '#6D14CC' : colors.night};
    font-weight: ${({ $selected }) => $selected ? '600' : '400'};
    font-size: ${fonts.sizes.small};
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;

    &:hover {
        background: ${({ $selected }) => $selected ? '#F7EFFF' : colors.neutral};
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const Select = styled.select`
    padding: 7px 10px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    font-size: ${fonts.sizes.small};
    font-family: inherit;
    cursor: pointer;
    max-width: 100%;

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: -1px;
    }
`;

const DateInput = styled.input`
    padding: 6px 10px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    font-size: ${fonts.sizes.small};
    font-family: inherit;

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: -1px;
    }
`;

const Separator = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
`;

const ExportButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    padding: 7px 14px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.night};
    font-size: ${fonts.sizes.small};
    font-family: inherit;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
        background: ${colors.neutral};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const ExportError = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.dangerDark};
`;
